"use client";

/**
 * The interactive part of /tools/php2shell-checker.
 *
 * Two ways to an answer. The automatic one asks /api/php2shell-check, which
 * reads the PHP version a site already announces in its headers. Many hosts
 * hide that header, so whenever the version stays hidden we say so plainly and
 * hand the visitor the manual routes (WordPress admin, hosting panel, SSH),
 * with a field to paste what they find. The pasted version is judged in the
 * browser by the same rules the API uses.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Globe,
  HelpCircle,
  Loader2,
  Server,
  ShieldAlert,
} from "lucide-react";
import {
  assessPhpVersion,
  parsePhpVersion,
  PHP2SHELL_FIXED_LABEL,
  type Php2ShellVerdict,
} from "@/lib/php2shell/versions";
import type { Php2ShellCheckResult } from "@/lib/php2shell/probe";
import type { HostingInfo } from "@/lib/php2shell/hosting";
import * as s from "@/components/schema-generator/styles";

type Props = {
  h1: React.ReactNode;
  intro: React.ReactNode;
  initialDomain?: string;
};

const VERDICT_UI: Record<
  Php2ShellVerdict,
  { title: string; tone: keyof typeof s.status; Icon: typeof ShieldAlert }
> = {
  vulnerable: { title: "Vulnerable to PHP2Shell", tone: "error", Icon: ShieldAlert },
  likely_vulnerable: {
    title: "Likely vulnerable to PHP2Shell",
    tone: "warning",
    Icon: AlertTriangle,
  },
  patched: { title: "Patched against PHP2Shell", tone: "success", Icon: CheckCircle2 },
};

const NON_PHP_PLATFORMS: Record<string, string> = {
  shopify: "Shopify",
  wix: "Wix",
  squarespace: "Squarespace",
  webflow: "Webflow",
};

export default function Php2ShellCheckerClient({ h1, intro, initialDomain = "" }: Props) {
  const [domain, setDomain] = useState(initialDomain);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Php2ShellCheckResult | null>(null);
  // Once we have a version, the manual check is no longer the next step, so it
  // folds behind a button at the bottom of the result instead of following it.
  const [manualOpen, setManualOpen] = useState(false);
  const manualRef = useRef<HTMLDivElement>(null);
  const autoRan = useRef(false);

  async function runCheck(value: string) {
    const trimmed = value.trim();
    if (!trimmed) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setManualOpen(false);
    try {
      const res = await fetch("/api/php2shell-check", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domain: trimmed }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.message || "Something went wrong. Try again.");
        return;
      }
      setResult(data as Php2ShellCheckResult);
    } catch {
      setError("Could not reach our server. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (initialDomain && !autoRan.current) {
      autoRan.current = true;
      void runCheck(initialDomain);
    }
  }, [initialDomain]);

  return (
    <div className="mx-auto w-full max-w-[1140px] px-6 py-12 lg:px-10 lg:py-16">
      <header className="mx-auto flex max-w-[860px] flex-col items-center gap-5">
        <h1 className={s.h1}>{h1}</h1>
        <div className={s.leadHero}>{intro}</div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void runCheck(domain);
          }}
          className="mt-2 flex w-full max-w-[620px] flex-col gap-2 sm:flex-row"
        >
          <label htmlFor="php2shell-domain" className="sr-only">
            Website address
          </label>
          <div className="relative flex-1">
            <Globe
              className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
              aria-hidden="true"
            />
            <input
              id="php2shell-domain"
              type="text"
              inputMode="url"
              autoComplete="off"
              spellCheck={false}
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="yourwebsite.com"
              disabled={loading}
              className={`${s.input} h-[52px] rounded-full pl-10`}
            />
          </div>
          <button
            type="submit"
            disabled={loading || !domain.trim()}
            className={`${s.btnPrimary} h-[52px]`}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                Checking
              </>
            ) : (
              "Check my site"
            )}
          </button>
        </form>

        <p className={`${s.bodySm} max-w-[560px] text-center`}>
          Read-only. We look at what your server already tells every visitor and never send
          an exploit or touch your files.
        </p>
      </header>

      <div className="mx-auto mt-8 max-w-[860px]" aria-live="polite">
        {loading && (
          <div className={`flex items-center gap-3 p-5 ${s.cardFlat}`}>
            <Loader2 className="h-5 w-5 animate-spin text-gray-500" aria-hidden="true" />
            <p className={s.body}>
              Looking for your PHP version on the homepage, the login page and a few other
              PHP-served addresses. This takes up to 10 seconds.
            </p>
          </div>
        )}

        {error && (
          <div className={`rounded-2xl border p-5 ${s.status.error.tint}`}>
            <p className={`text-[14.5px] ${s.status.error.text}`}>{error}</p>
          </div>
        )}

        {result && (
          <ResultCard
            result={result}
            onManual={() => {
              setManualOpen(true);
              // Wait a frame so a just-opened section exists before scrolling.
              requestAnimationFrame(() =>
                manualRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
              );
            }}
          />
        )}
      </div>

      {(!result?.assessment || manualOpen) && (
        <div ref={manualRef} className="mx-auto mt-12 max-w-[860px] scroll-mt-24">
          <ManualCheck highlight={Boolean(result && !result.assessment)} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Result
// ---------------------------------------------------------------------------

function ResultCard({
  result,
  onManual,
}: {
  result: Php2ShellCheckResult;
  onManual: () => void;
}) {
  const { assessment } = result;

  if (assessment) {
    const ui = VERDICT_UI[assessment.verdict];
    const tone = s.status[ui.tone];
    return (
      <div className={`overflow-hidden ${s.card}`}>
        <div className={`flex items-start gap-3 border-b p-5 sm:p-6 ${tone.tint}`}>
          <ui.Icon className={`mt-0.5 h-6 w-6 shrink-0 ${tone.icon}`} aria-hidden="true" />
          <div>
            <p className={`text-[13px] font-semibold uppercase tracking-wider ${tone.text}`}>
              {result.domain}
            </p>
            <h2 className="mt-1 font-sans text-[22px] font-medium leading-tight tracking-[-0.02em] text-nws-ink sm:text-[26px]">
              {ui.title}
            </h2>
            <p className="mt-1.5 text-[15px] text-nws-ink">
              Running PHP <span className="font-mono font-semibold">{assessment.version}</span>
            </p>
          </div>
        </div>
        <div className="space-y-4 p-5 sm:p-6">
          <p className={s.body}>{assessment.reason}</p>
          <HostingLine hosting={result.hosting} />
          {assessment.verdict !== "patched" && (
            <FixAdvice hosting={result.hosting} fixedIn={assessment.fixedIn} />
          )}
          <Evidence result={result} />
          <p className={s.bodySm}>
            A version header is strong evidence, not proof: hosts can patch without changing
            it, and a CDN can report a different server.
          </p>
          <div className="border-t border-nws-grey-medium pt-4">
            <button type="button" onClick={onManual} className={s.btnSecondarySm}>
              Check the version by hand instead
            </button>
          </div>
        </div>
      </div>
    );
  }

  // No version visible.
  let title: string;
  let body: React.ReactNode;
  const nonPhp = NON_PHP_PLATFORMS[result.platform];

  if (result.unreachable) {
    title = "We could not read your site";
    body = `${result.domain} did not answer, or answered with a bot check. That says nothing about PHP2Shell, so check the version by hand below.`;
  } else if (nonPhp) {
    title = `${nonPhp} sites are not affected`;
    body = `${result.domain} runs on ${nonPhp}, a hosted platform that does not run your site on PHP you manage. PHP2Shell is a flaw in PHP itself, so there is nothing for you to patch here.`;
  } else if (result.runsPhp) {
    title = "Your site runs PHP, but hides its version";
    body = (
      <>
        {result.domain} runs on PHP (we found {joinList(result.phpSignals)}), but your server
        does not announce which version. Hiding it is good practice. It just means you
        need to look it up yourself, which takes about a minute with one of the methods below.
      </>
    );
  } else {
    title = "We could not find a PHP version";
    body = `${result.domain} does not show any sign of PHP from the outside. If you know the site runs on PHP (WordPress, Joomla, Drupal, Laravel and most shared hosting do), check the version by hand below.`;
  }

  return (
    <div className={`overflow-hidden ${s.card}`}>
      <div
        className={`flex items-start gap-3 border-b p-5 sm:p-6 ${
          nonPhp ? s.status.success.tint : s.status.neutral.tint
        }`}
      >
        {nonPhp ? (
          <CheckCircle2 className={`mt-0.5 h-6 w-6 shrink-0 ${s.status.success.icon}`} aria-hidden="true" />
        ) : (
          <HelpCircle className={`mt-0.5 h-6 w-6 shrink-0 ${s.status.neutral.icon}`} aria-hidden="true" />
        )}
        <div>
          <p className="text-[13px] font-semibold uppercase tracking-wider text-gray-600">
            {result.domain}
          </p>
          <h2 className="mt-1 font-sans text-[22px] font-medium leading-tight tracking-[-0.02em] text-nws-ink sm:text-[26px]">
            {title}
          </h2>
        </div>
      </div>
      <div className="space-y-4 p-5 sm:p-6">
        <p className={s.body}>{body}</p>
        {!nonPhp && <HostingLine hosting={result.hosting} />}
        {!nonPhp && !result.unreachable && result.runsPhp && (
          <FixAdvice hosting={result.hosting} fixedIn={null} unknownVersion />
        )}
        {!nonPhp && (
          <button type="button" onClick={onManual} className={s.btnSecondary}>
            Show me how to check by hand
          </button>
        )}
      </div>
    </div>
  );
}

/** "Hosted on Kinsta, behind Cloudflare", with what gave it away. */
function HostingLine({ hosting }: { hosting: HostingInfo }) {
  const { host, cdn } = hosting;
  if (!host && !cdn) return null;
  return (
    <div className="flex items-start gap-2.5">
      <Server className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
      <p className="text-[14.5px] leading-relaxed text-nws-ink">
        {host ? (
          <>
            Hosted on <span className="font-semibold">{host.name}</span>
            {cdn && <>, behind {cdn}</>}.{" "}
            <span className="text-gray-600">We can tell from {host.via}.</span>
          </>
        ) : (
          <>
            Served through <span className="font-semibold">{cdn}</span>, which hides the hosting
            company behind it.
          </>
        )}
      </p>
    </div>
  );
}

/**
 * The next step depends on who runs PHP: the provider on managed hosting, the
 * owner on their own server. We only know when HostingLine named a host.
 */
function FixAdvice({
  hosting,
  fixedIn,
  unknownVersion = false,
}: {
  hosting: HostingInfo;
  fixedIn: string | null;
  unknownVersion?: boolean;
}) {
  const target = fixedIn
    ? `PHP ${fixedIn} or later`
    : `one of the fixed releases, ${PHP2SHELL_FIXED_LABEL}`;
  const host = hosting.host;
  let advice: React.ReactNode;

  if (host?.kind === "managed") {
    advice = (
      <>
        {host.name} runs PHP for you, so this is theirs to update. Ask their support whether your
        site&apos;s PHP has the PHP2Shell fixes, meaning {target}. If your dashboard lets you
        choose a PHP version, switching to a fixed one does the same.
      </>
    );
  } else if (host?.kind === "panel") {
    advice = (
      <>
        Your server runs the Plesk control panel. On shared hosting, ask your host to update PHP
        to {target}. On a server you run yourself, install the update from Plesk&apos;s{" "}
        <span className="font-semibold">Tools &amp; Settings → Updates</span> and restart PHP.
      </>
    );
  } else if (host?.kind === "cloud") {
    advice = (
      <>
        This is a {host.name} server. Unless a service such as Cloudways or RunCloud manages it for
        you, updating PHP is up to whoever runs it: install {target} and restart PHP.{" "}
        <a href="#fix" className={s.link}>
          The steps are below
        </a>
        .
      </>
    );
  } else {
    advice = (
      <>
        If a hosting company runs your site, ask them to update PHP to {target}. If you run the
        server yourself, update PHP and restart it.{" "}
        <a href="#fix" className={s.link}>
          Both routes are below
        </a>
        .
      </>
    );
  }

  return (
    <p className={s.body}>
      <span className="font-semibold text-nws-ink">
        {unknownVersion ? "If your version turns out to be old: " : "What to do: "}
      </span>
      {advice}
    </p>
  );
}

function Evidence({ result }: { result: Php2ShellCheckResult }) {
  return (
    <div className={`p-4 ${s.cardInset}`}>
      <p className={s.eyebrow}>Where we found it</p>
      <ul className="mt-2 space-y-1.5">
        {result.evidence.map((e) => (
          <li key={`${e.source}-${e.version}`} className="text-[13.5px] text-nws-grey-text">
            <span className="font-mono text-nws-ink">PHP {e.version}</span> from the {e.source}{" "}
            on {pathSummary(e.paths)}
          </li>
        ))}
      </ul>
      {result.phpinfoExposed && (
        <p className={`mt-3 text-[13.5px] ${s.status.warning.text}`}>
          Your site has a public phpinfo() page at {result.phpinfoExposed}. It shows your
          server&apos;s full configuration to anyone. Delete it.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Manual check
// ---------------------------------------------------------------------------

type Method = "wordpress" | "hosting" | "ssh";

const METHODS: Array<{ id: Method; label: string }> = [
  { id: "wordpress", label: "WordPress admin" },
  { id: "hosting", label: "Hosting panel" },
  { id: "ssh", label: "SSH" },
];

function ManualCheck({ highlight }: { highlight: boolean }) {
  const [method, setMethod] = useState<Method>("wordpress");

  return (
    <section
      id="manual-check"
      className={`overflow-hidden ${s.card} ${highlight ? "ring-2 ring-nws-ink/15" : ""}`}
    >
      <div className="border-b border-nws-grey-medium px-4 py-4 sm:px-6 sm:py-5">
        <h2 className="font-sans text-[16px] font-semibold text-nws-ink sm:text-[17px]">
          Check your PHP version by hand
        </h2>
        <p className={`mt-0.5 ${s.bodySm}`}>
          When your server hides its version, this is the reliable way. Pick whichever access
          you have, then paste the version below.
        </p>
      </div>

      <div className="px-4 pb-6 pt-5 sm:px-6">
        <div role="tablist" aria-label="How to check" className="flex flex-wrap gap-2">
          {METHODS.map((m) => (
            <button
              key={m.id}
              type="button"
              role="tab"
              aria-selected={method === m.id}
              onClick={() => setMethod(m.id)}
              className={
                method === m.id
                  ? "rounded-full bg-black px-4 py-1.5 font-sans text-[13px] font-medium text-white"
                  : s.btnSecondarySm
              }
            >
              {m.label}
            </button>
          ))}
        </div>

        <div role="tabpanel" className="mt-5">
          {method === "wordpress" && (
            <Steps
              steps={[
                <>Log in to your WordPress dashboard as an administrator.</>,
                <>
                  Go to <Path>Tools → Site Health</Path> and open the <Path>Info</Path> tab.
                </>,
                <>
                  Expand the <Path>Server</Path> section. The line <Path>PHP version</Path>{" "}
                  shows exactly what your site runs, for example{" "}
                  <Mono>8.3.12 (Supports 64bit values)</Mono>.
                </>,
                <>
                  No Site Health menu? It was added in WordPress 5.2, so an older install is a
                  sign PHP is old too. Use the hosting panel or SSH instead.
                </>,
              ]}
            />
          )}

          {method === "hosting" && (
            <Steps
              steps={[
                <>
                  <span className="font-semibold text-nws-ink">cPanel:</span> open{" "}
                  <Path>Software → MultiPHP Manager</Path> (or <Path>Select PHP Version</Path>).
                  The version next to your domain is the one serving it.
                </>,
                <>
                  <span className="font-semibold text-nws-ink">Plesk:</span> open{" "}
                  <Path>Websites &amp; Domains → PHP</Path> (or <Path>PHP Settings</Path>) for the
                  domain.
                </>,
                <>
                  <span className="font-semibold text-nws-ink">Managed WordPress hosts</span>{" "}
                  (WP Engine, Kinsta, SiteGround and similar) show it in the site&apos;s settings
                  or tools page, usually labelled <Path>PHP version</Path>.
                </>,
                <>
                  Panels often show only the branch, such as <Mono>8.3</Mono>. That is not
                  enough to judge: ask your host which patch release is installed and whether
                  it includes the PHP2Shell fixes.
                </>,
              ]}
            />
          )}

          {method === "ssh" && (
            <div className="space-y-4">
              <Steps
                steps={[
                  <>
                    Run <Mono>php -v</Mono>. This shows the command-line PHP, which is not
                    always the one serving your website.
                  </>,
                  <>
                    To see the version the web server really uses, check the running workers:
                  </>,
                ]}
              />
              <CodeBlock>{`ps aux | grep -E "php-fpm|lsphp|php-cgi" | grep -v grep
php-fpm8.3 -v   # adjust to the version in the process name`}</CodeBlock>
              <Steps
                start={3}
                steps={[
                  <>
                    Or ask the web server directly. Create a one-line file in your site&apos;s
                    web root, open it in a browser, then delete it straight away:
                  </>,
                ]}
              />
              <CodeBlock>{`echo '<?php echo PHP_VERSION;' > php-version-check.php
# visit https://yourwebsite.com/php-version-check.php, then:
rm php-version-check.php`}</CodeBlock>
              <Steps
                start={4}
                steps={[
                  <>
                    On Debian, Ubuntu, RHEL, AlmaLinux or Rocky, your distribution may have
                    backported the fix without changing the version number. Check the full
                    package version against your distribution&apos;s security advisory:
                  </>,
                ]}
              />
              <CodeBlock>{`dpkg -l | grep -E "^ii\\s+php[0-9.]*-(fpm|cli|common)"   # Debian / Ubuntu
rpm -qa | grep -E "^php(-fpm|-cli|-common)?-[0-9]"       # RHEL / Alma / Rocky`}</CodeBlock>
            </div>
          )}
        </div>

        <ManualVersionInput />
      </div>
    </section>
  );
}

function ManualVersionInput() {
  const [value, setValue] = useState("");
  const parsed = useMemo(() => (value.trim() ? parsePhpVersion(value) : null), [value]);
  const assessment = parsed ? assessPhpVersion(parsed) : null;
  const ui = assessment ? VERDICT_UI[assessment.verdict] : null;

  return (
    <div className={`mt-6 p-4 sm:p-5 ${s.cardInset}`}>
      <label htmlFor="php2shell-manual" className={s.label}>
        Paste the version you found
      </label>
      <input
        id="php2shell-manual"
        type="text"
        autoComplete="off"
        spellCheck={false}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="e.g. 8.3.12 or PHP 8.1.2-1ubuntu2.20"
        className={`${s.input} mt-2 font-mono`}
      />
      {value.trim() && !parsed && (
        <p className={`mt-2 ${s.bodySm}`}>
          That does not look like a PHP version. It should read like 8.3.12 or 7.4.33.
        </p>
      )}
      {assessment && ui && parsed && (
        <div className={`mt-3 flex items-start gap-2.5 rounded-xl border p-3.5 ${s.status[ui.tone].tint}`}>
          <ui.Icon className={`mt-0.5 h-5 w-5 shrink-0 ${s.status[ui.tone].icon}`} aria-hidden="true" />
          <div>
            <p className={`text-[14.5px] font-semibold ${s.status[ui.tone].text}`}>
              PHP {parsed.raw}: {ui.title}
            </p>
            <p className="mt-1 text-[13.5px] leading-relaxed text-nws-grey-text">
              {assessment.reason}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small pieces
// ---------------------------------------------------------------------------

/** "the homepage, /wp-login.php and 5 more addresses" */
function pathSummary(paths: string[]): string {
  if (paths.length <= 3) return joinList(paths);
  const rest = paths.length - 2;
  return `${paths.slice(0, 2).join(", ")} and ${rest} more addresses`;
}

function joinList(items: string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

function Steps({ steps, start = 1 }: { steps: React.ReactNode[]; start?: number }) {
  return (
    <ol className="space-y-3">
      {steps.map((step, i) => (
        <li key={i} className="flex gap-3">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-nws-grey-light font-sans text-[12px] font-semibold text-nws-ink">
            {start + i}
          </span>
          <p className="pt-0.5 text-[14.5px] leading-relaxed text-nws-grey-text">{step}</p>
        </li>
      ))}
    </ol>
  );
}

function Path({ children }: { children: React.ReactNode }) {
  return <span className="font-semibold text-nws-ink">{children}</span>;
}

function Mono({ children }: { children: React.ReactNode }) {
  return (
    <code className="rounded bg-nws-grey-light px-1.5 py-0.5 font-mono text-[13px] text-nws-ink">
      {children}
    </code>
  );
}

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="overflow-x-auto rounded-xl bg-nws-ink p-4 font-mono text-[12.5px] leading-relaxed text-white">
      <code>{children}</code>
    </pre>
  );
}
